import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ITotalAccruedInterest } from '../types';
import {
  getApiErrorMessage,
  getSavingsAuthConfig,
  getSavingsBusinessId,
} from '../utils';

interface IGetTotalAccruedInterestByUserResponse {
  status?: boolean;
  message?: string;
  data?: ITotalAccruedInterest;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetTotalAccruedInterestByUserResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getTotalAccruedInterestByUserAsync = createAsyncThunk(
  'savings/interest/totalAccruedByUser',
  async (_: void, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const businessId = getSavingsBusinessId(state);

      const { data } = await api.get<IGetTotalAccruedInterestByUserResponse>(
        `/savings/interest-payments/total-accrued-interest-by-userId/${businessId}`,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const totalAccruedInterestByUserSlice = createSlice({
  name: 'savings/interest/totalAccruedByUser',
  initialState,
  reducers: {
    resetTotalAccruedInterestByUser(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTotalAccruedInterestByUserAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getTotalAccruedInterestByUserAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetTotalAccruedInterestByUserResponse;
      })
      .addCase(getTotalAccruedInterestByUserAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetTotalAccruedInterestByUser } =
  totalAccruedInterestByUserSlice.actions;

export default totalAccruedInterestByUserSlice.reducer;
